import {
  bankTeamAnswerSchema,
  fileMovedSchema,
  reassignmentPreviewSchema,
  teamJournalSchema,
  type TeamJournal,
  transferDoneSchema,
  transferPreviewSchema,
  invitationTokenFrom,
  bankPolicyWriteSchema,
  type NotificationsAnswer,
  PASSWORD_RESET_ROUTE,
  passwordResetTokenFrom,
  type ReadAllAnswer,
  type ReadMarkAnswer,
  type OwnFile,
} from "@hakili/contracts";
import { t } from "@hakili/i18n";

const DASH_NAME = t("common.valeur.tiret");
import { find, resolveRoute, useLocation } from "@hakili/app-shell";
import { DemandeFonctionnaliteProvider, Toast, ToastStack } from "@hakili/ui";
import { ApiError } from "@hakili/api-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Composer,
  composerFactsFrom,
  type ComposerFactsView,
  ownFileFrom,
  reviewCompleteFrom,
  storedOfferFrom,
  type StoredOfferView,
} from "./routes/composer.js";
import { type AnalysisView, analysisFrom, analysisSentenceFrom } from "./routes/analyse.js";
import {
  addressOf,
  comportementOf,
  committeeAddress,
  decisionAddress,
  decisionComportementAddress,
  dossierAddress,
  dossierComportementAddress,
  ECRANS,
  landingOf,
  ownerOf,
  shellScreenOf,
  tableFor,
} from "./addresses.js";
import { decisionFileOfAddress, decisionTargetId } from "./decision-target.js";
import { Connexion } from "./routes/connexion.js";
import { MotDePasseOublie } from "./routes/mot-de-passe-oublie.js";
import { type LienLu, NouveauMotDePasse } from "./routes/nouveau-mot-de-passe.js";
import {
  Decision,
  type DecisionBody,
  type DecisionFileView,
  DecisionPending,
  readDecisionAnswer,
  ratePreviewFrom,
  Decisions,
  queueFrom,
  recordingFileOf,
  type QueueView,
} from "./routes/decisions.js";
import { Committee, type CommitteeBody, noticeOf } from "./routes/comite.js";
import { CommitteeWait } from "./routes/committee-wait.js";
import { BehaviourTab } from "./behaviour/BehaviourTab.js";
import { DossierTabs } from "./behaviour/DossierTabs.js";
import { DecisionHead } from "./routes/decision-head.js";
import { Dossiers, fichierFrom, fichiersFrom, fill, type FichierBanque } from "./routes/dossiers.js";
import { Invitation } from "./routes/invitation.js";
import { type MotifPage, PageRefusee } from "./routes/page-refusee.js";
import { DocumentViewer, documentFilesFrom, type DocumentFileView } from "./routes/document-viewer.js";
import { receivedDocuments, reportFilesFrom } from "./routes/conversation-documents.js";
import { Equipe, type TeamRead } from "./routes/equipe.js";
import { filterNameOf } from "./team-read.js";
import { ENTRUSTED_KIND, Pipeline } from "./routes/pipeline.js";
import {
  AskEntry,
  type QuestionExchangeView,
  exchangesCounted,
  threadFrom,
} from "./routes/question-panel.js";
import { WideConversation } from "./routes/wide-conversation.js";
import {
  Politique,
  type PolicyNotice,
  type PolicySaved,
  type PolicyRead,
  type PolicyWrite,
} from "./routes/politique.js";
import {
  Instructions,
  pointsFrom,
  progressionFrom,
  Revue,
  RetourPanel,
  type PointRevue,
  type ProgressionRevue,
  refusalFrom,
  type RefusalRead,
} from "./routes/revue.js";
import {
  type FileOfferRead,
  fileOfferFrom,
  LaterStagePanel,
  OfferPanel,
  ValidationPanel,
} from "./routes/validation.js";
import { type SignatureRead, SignaturePanel, signatureFrom } from "./routes/signature.js";
import { type BankReportView, ReportPanel, reportFrom } from "./routes/report-panel.js";
import { bayContextFor, runStanding, UnderwriterBay } from "./routes/underwriter-bay.js";
import {
  type ConditionsRead,
  ConditionsPanel,
  conditionsFrom,
  type DisbursementRead,
  disbursementFrom,
  type LaterObligation,
  laterObligationsFrom,
} from "./routes/conditions.js";
import { useFundsMoment } from "./routes/funds-moment.js";
import { DecaissePanel, type OrdreRead, ordreFrom } from "./routes/ordre.js";
import { transmittedAtFrom } from "./routes/parcours-card.js";
import { Briefing } from "./routes/briefing.js";
import { lastOfferFrom, PerduView, RefuseView } from "./routes/clos.js";
import { closReading, Synthese } from "./routes/synthese.js";
import { Workspace } from "./routes/workspace.js";
import { OfferReadFailure } from "./routes/offer-read-failure.js";
import { personaDe, useSession } from "./session/useSession.js";
import { useNoticeTimer } from "./notices.js";
import { BankShell, type BankScreen } from "./shell/BankShell.js";
import { PersonaSwitcher } from "./shell/PersonaSwitcher.js";
import type { Persona } from "./shell/personas.js";

/**
 * The bank's app, assembled , `P1-BKW-001` to `P1-BKW-005` (round B), and
 * since round `L-0` the five screens of August to their drawing
 * (`hakili-bank.jsx`): the login's card, the shell with its rail, the
 * pipeline with its tiles and tabs, the dossiers with their search and
 * filter, the workspace with its header, stepper, reception banner and the
 * review in three steps.
 *
 * **The screen is the ADDRESS** (`D-1020-4`, round `BANK-ROUTES`, 20 October 2026). Round B chose « not a router,
 * still »: a selection held here, a reload back to the landing. That sentence is kept and stands superseded by
 * the founder's ruling: the screen is read from the location, on the console's own machinery
 * (`@hakili/app-shell`'s `useLocation` and `resolveRoute`, the bank's table in `addresses.ts`), and a file is
 * named by its reference. The open file is DERIVED at read time from the lists the server most recently sent
 * (`P1-CNW-025`), never captured at click time.
 *
 * **Nothing is fetched before there is a session**, as before.
 */

const ORIGIN: string = import.meta.env.VITE_API_ORIGIN;

/** The chef's landing: the queue read on arrival, by his own door. */
function HeadDecisions(props: {
  readonly fullName: string;
  readonly queue: QueueView | null;
  /** The queue's own loader , not the files' `charger`, whose four invocations `App.spec.tsx` guards. */
  readonly lire: () => Promise<void>;
  readonly onOpen: (fileId: string) => void;
  readonly onCommittee: (reference: string) => void;
}): React.JSX.Element {
  const { lire, queue } = props;
  useEffect(() => {
    if (queue === null) void lire().catch(() => undefined);
  }, [lire, queue]);
  if (queue === null) return <p className="p-6 text-sm text-gray-500">{t("bank.session.chargement")}</p>;
  return (
    <Decisions
      fullName={props.fullName}
      queue={queue}
      onOpen={props.onOpen}
      onCommittee={props.onCommittee}
    />
  );
}

/** Round `L-3`, `M1`: the standing return as the file's read carries it. */
interface CompletionRead {
  readonly requestedAt: string;
  readonly message: string;
  readonly clientMustAct: boolean;
  readonly items: readonly { readonly label: string; readonly documentCode: string | null }[];
}
function completionFrom(answer: unknown): CompletionRead | null {
  const raw = (answer as { completion?: unknown } | null)?.completion;
  if (typeof raw !== "object" || raw === null) return null;
  const one = raw as Record<string, unknown>;
  if (typeof one["requestedAt"] !== "string" || typeof one["message"] !== "string") return null;
  return {
    requestedAt: one["requestedAt"],
    message: one["message"],
    clientMustAct: one["clientMustAct"] === true,
    items: Array.isArray(one["items"])
      ? one["items"].map((item: unknown) => ({
          label:
            typeof (item as { label?: unknown }).label === "string" ? (item as { label: string }).label : "",
          documentCode:
            typeof (item as { documentCode?: unknown }).documentCode === "string"
              ? (item as { documentCode: string }).documentCode
              : null,
        }))
      : [],
  };
}
/** `L-4` (F7): why the file is at `perdu`, the row's own reason , the banner's copy. */
function lostFrom(answer: unknown): { reason: string; at: string; detail: string | null } | null {
  const raw = (answer as { lost?: unknown } | null)?.lost;
  if (raw === null || typeof raw !== "object") return null;
  const row = raw as { reason?: unknown; at?: unknown; detail?: unknown };
  return typeof row.reason === "string"
    ? {
        reason: row.reason,
        at: typeof row.at === "string" ? row.at : "",
        // `K-2` (resumed): the honest reason line when the seller's breach closed the file.
        detail: typeof row.detail === "string" ? row.detail : null,
      }
    : null;
}
/** `L-3b`, `M1`: 05 §2.6.5's closed list as the door serves it, for the modal. */
function reasonsFrom(answer: unknown): readonly { code: string; label: string }[] {
  const raw = (answer as { refusalReasons?: unknown } | null)?.refusalReasons;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((one: unknown) =>
    typeof (one as { code?: unknown }).code === "string" &&
    typeof (one as { label?: unknown }).label === "string"
      ? [{ code: (one as { code: string }).code, label: (one as { label: string }).label }]
      : [],
  );
}

/**
 * `WS-2` (`WORKSPACE-STALE-READS`): **every read of the open file rides with the id it was read for**, and is drawn only under that file's own address.
 * One record, written by `putReads` for a file id: a read for any other file starts the record again, so nothing of X is ever handed to Y's panels,
 * whatever the order the answers come in (`syntheseFacts` was the first of its kind and keeps its own `fileId`).
 */
interface FileReads {
  readonly fileId: string | null;
  readonly points: readonly PointRevue[];
  readonly progression: ProgressionRevue | null;
  /** `L-1`, `M3`: the composer's facts and the standing composition, read from the door once the review is complete. */
  readonly composer: {
    readonly facts: ComposerFactsView;
    readonly stored: StoredOfferView | null;
    readonly reviewComplete: boolean;
    /** `CHEF-SELF-VALIDATION` 2a: what the server serves the chef who holds the file (`null` for an analyste, and for a server that serves none). */
    readonly ownFile: OwnFile | null;
  } | null;
  /** `L-1b`: the offer's read at every stage , the chef's decision, the instructions, the send's stamps. */
  readonly offerRead: FileOfferRead | null;
  readonly offerTransmittedAt: string | null;
  readonly banksReached: number | null;
  readonly completion: CompletionRead | null;
  readonly refusalRead: RefusalRead | null;
  readonly refusalReasons: readonly { code: string; label: string }[];
  readonly lostRead: { reason: string; at: string; detail: string | null } | null;
  /** `T-1`, `M1`: the signature as the file's read carries it (the pack, the appointment, the scans). */
  readonly signatureRead: SignatureRead | null;
  /** Lane E, M2: the report of the open file (paper, Hakili's sheet, provenance, validity). */
  readonly reportRead: BankReportView | null;
  /** `T-1b`, `M1`: the conditions, the obligations after and the disbursement instruction, the file's read. */
  readonly conditionsRead: ConditionsRead | null;
  readonly disbursementRead: DisbursementRead | null;
  readonly laterObligations: readonly LaterObligation[];
  /** `T-2`: the ordre's read at the conditions and after the funds. */
  readonly ordreRead: OrdreRead | null;
  /** `SYNTHESE-READ-FAILURE`: the offer door FAILED for this file (not late, not empty); its figures are withheld under a notice. */
  readonly offerFailed: boolean;
}

/** `SYNTHESE-READ-FAILURE` (B): the stages whose panels are fed by the offer door; they draw nothing under the failure notice, never a panel on nothing. */
const OFFER_PANEL_STAGES: ReadonlySet<string> = new Set([
  "revue",
  "validation",
  "offre",
  "comite",
  "signature",
]);

const NO_READS: FileReads = {
  fileId: null,
  points: [],
  progression: null,
  composer: null,
  offerRead: null,
  offerTransmittedAt: null,
  banksReached: null,
  completion: null,
  refusalRead: null,
  refusalReasons: [],
  lostRead: null,
  signatureRead: null,
  reportRead: null,
  conditionsRead: null,
  disbursementRead: null,
  laterObligations: [],
  ordreRead: null,
  offerFailed: false,
};

/** `DECISION-ANALYSIS-SCOPE`: one analysis read, with the id of the file it was read for (the sibling of `FileReads`: the Décision route writes it too). */
interface AnalysisRead {
  readonly fileId: string | null;
  readonly analysis: AnalysisView | null;
  readonly sentence: string | null;
  readonly unavailable: boolean;
}

const NO_ANALYSIS: AnalysisRead = { fileId: null, analysis: null, sentence: null, unavailable: false };

export function App(): React.JSX.Element {
  const { session, expired, connecter, deconnecter, api, submitFeatureRequest } = useSession(ORIGIN);
  const [pipeline, setPipeline] = useState<readonly FichierBanque[]>([]);
  const [liste, setListe] = useState<readonly FichierBanque[]>([]);
  const [archives, setArchives] = useState<readonly FichierBanque[]>([]);
  // `D-1020-4`: the screen is the ADDRESS (`addresses.ts`), read below from the location, no longer a selection held here.
  const { path, naviguer } = useLocation();
  const [listsLoaded, setListsLoaded] = useState(false);
  /** Frame 04: the first read of the lists failed; the page says so and offers « Réessayer » (never a spinner that never ends). */
  const [listsFailed, setListsFailed] = useState(false);
  const [ouvertId, setOuvertId] = useState<string | null>(null);
  /** `L-0`: the open file's own read , its points and progress ride it (a read, not a take). */
  const [ouvertLu, setOuvertLu] = useState<FichierBanque | null>(null);
  /** `CORRIDOR-439`: the file whose OWN door answered 404 for her, the only thing that says « introuvable » (a list snapshot is a cache, `A47`). */
  const [refusedFileId, setRefusedFileId] = useState<string | null>(null);
  /** `#778`: what the composer's live composition says to the bay's face (`true` it holds a block or a gap, `false` none, `null` no composition stands). */
  const [composerFlagging, setComposerFlagging] = useState<boolean | null>(null);
  const [reads, setReads] = useState<FileReads>(NO_READS);
  const putReads = useCallback((fileId: string, patch: Partial<Omit<FileReads, "fileId">>): void => {
    setReads((prev) => ({ ...(prev.fileId === fileId ? prev : { ...NO_READS, fileId }), ...patch }));
  }, []);
  // `S3` (D2 `app.js:203`): « Revenir à la revue » shows the review while the file stays at `revue`; a view toggle, never a door.
  const [backToReview, setBackToReview] = useState<string | null>(null);
  /**
   * `U-2`, `M3`: Hakili's analysis, its door's read , `null` with no run, the sentence beside; `F-2`: whether the door did not answer (the service, not the run).
   * `DECISION-ANALYSIS-SCOPE`: the record carries the id of the file it was read for, and a screen draws it only when that id is the file it draws
   * (`analysisOf`); before that the bay shows its skeleton, never another file's note.
   */
  const [analysisRead, setAnalysisRead] = useState<AnalysisRead>(NO_ANALYSIS);
  /** `L-1`, `M3`: the composer's facts and the standing composition, read from the door once the review is complete. */
  /** `L-1b`: the offer's read at every stage , the chef's decision, the instructions, the send's stamps. */
  /** `A-1b-2a`: the offer door's facts, read at EVERY stage for the synthesis (the door answers at « nouveau » too, proven on the park). */
  const [syntheseFacts, setSyntheseFacts] = useState<{
    readonly fileId: string;
    readonly facts: ComposerFactsView | null;
  } | null>(null);
  /** `L-1b`, `M4`: the chef's queue and the file he opened. */
  const [queue, setQueue] = useState<QueueView | null>(null);
  const [decisionId, setDecisionId] = useState<string | null>(null);
  /** `ARC2` (D2 `usepropo`): how many times the bay's « Reprendre la proposition » was pressed; the composer reads the count. */
  const [proposalTaken, setProposalTaken] = useState(0);
  const [decisionFile, setDecisionFile] = useState<DecisionFileView | null>(null);
  /**
   * `D2` frame 62's sub-line, from `ARC-3`'s two sources: the offer door's `facts.vehicleYear` and the FILE door's `durationMonths` (the term the client
   * asked). The chef's role holds both doors (`voirPipelinePersonnel`, chef « oui »); a door that does not answer leaves its part out.
   */
  const [decisionHead, setDecisionHead] = useState<{
    readonly fileId: string;
    readonly vehicleYear: number | null;
    readonly requestedMonths: number | null;
  } | null>(null);
  /**
   * `COMITE-RECORD-2`: the file « Passage en comité » reads, re-read on every arrival and after every recording. `COMITE-STALE-FILE`: it rides with the id
   * of the file it was read for, and is forgotten the moment the screen is left, so the screen is drawn only from the read of THIS opening.
   */
  const [committeeRead, setCommitteeRead] = useState<{
    readonly fileId: string;
    readonly file: DecisionFileView;
  } | null>(null);
  /** `COMITE-STALE-FILE`: the newest read of the committee screen; an older answer landing late is dropped. */
  const committeeReadSeq = useRef(0);
  /** `PARSER-GAPCOUNT` (A47): the file whose decision answer broke the contract (a required field missing or mistyped); the screen says so by name. */
  const [decisionBroken, setDecisionBroken] = useState<string | null>(null);
  const [committeeNotice, setCommitteeNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** `ODF-1`: the file's own read is running; the décaissé card shows « le détail arrive » while the ordre's read is on its way. */
  const [ordreEnLecture, setOrdreEnLecture] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /**
   * `RESET-PASSWORD` R2 (frames 100 to 109): the sign-in's « Mot de passe oublié » page, and the page a reset link lands on.
   * The link is `/nouveau-mot-de-passe#{token}`; its token is read ONCE here (a pure read, so a double render cannot spend it)
   * and the fragment is stripped from the address bar by the effect below, so the token is never again in a URL and reaches the
   * doors only in a POST body (`password-reset-path.ts`, Q4).
   */
  const [oubli, setOubli] = useState(false);
  const [reinitFaite, setReinitFaite] = useState(false);
  const [lienReinit, setLienReinit] = useState<{ readonly token: string | null } | null>(() =>
    globalThis.location.pathname === PASSWORD_RESET_ROUTE
      ? { token: passwordResetTokenFrom(globalThis.location.hash) }
      : null,
  );
  useEffect(() => {
    if (globalThis.location.pathname === PASSWORD_RESET_ROUTE && globalThis.location.hash !== "") {
      globalThis.history.replaceState(null, "", PASSWORD_RESET_ROUTE);
    }
  }, []);
  const lireLienReinit = useCallback(
    (token: string): Promise<LienLu> =>
      api.request<LienLu>("/api/v1/auth/password/reset-check", "post", { body: { token } }),
    [api],
  );
  /** Leaves the reset link for the sign-in, which is at `/`: the page a link opened is not a page to reload. */
  const quitterReinit = useCallback((): void => {
    globalThis.history.replaceState(null, "", "/");
    // `replaceState` tells the router nothing: without this the router's path stays at the reset link while the bar reads « / ».
    globalThis.dispatchEvent(new PopStateEvent("popstate"));
    setLienReinit(null);
    setOubli(false);
  }, []);
  // `GRADE-C` frame 124: the toast after a policy save the door accepted; it leaves with the screen.
  const [policyNotice, setPolicyNotice] = useState<PolicyNotice | null>(null);
  /** `KIT-TOAST-D2` 3b: the PUT's answer about the grid's highest row (the callout's source); cleared when the screen is left. */
  const [policySaved, setPolicySaved] = useState<PolicySaved | null>(null);
  // D2 (122): the analyst's crumb « Offre indicative › Politique » returns to the file whose composer sent her there.
  const [policyFrom, setPolicyFrom] = useState<string | null>(null);
  /** The drawing's « Vue » device, behind the demo flag alone (4.7.3); the persona is the ROLE's otherwise. */
  const [personaDemo, setPersonaDemo] = useState<Persona | null>(null);

  const authentifie = session.status === "authenticated";
  /**
   * `FX-3`, `M3` (`S2-4`): **one letter, and two banks stood unopenable for a week.**
   *
   * This read `/invitations/` , plural , while the mail writes `/invitation/{token}` and the three other
   * apps match the singular. A bank's invitee therefore fell through to the login page: « Bank invitation
   * link lands on the login page, never on password creation; HB Bank and Hbank stay "Invitation envoyée",
   * 0 users ». The path is now read through `@hakili/contracts`, the one author of it.
   */
  const jeton = invitationTokenFrom(globalThis.location.pathname);

  // ---- the address (`D-1020-4`, `BANK-ROUTES`): console's machinery, the bank's table.
  const roleNow = session.status === "authenticated" ? personaDe(session.identity.role) : null;
  const routesPersona: Persona = personaDemo ?? roleNow ?? "analyste";
  const resolution = useMemo(
    () => resolveRoute(tableFor(routesPersona), path, ECRANS[routesPersona]),
    [routesPersona, path],
  );
  const routeScreen = resolution.kind === "render" ? resolution.route.screen : null;
  const routeReference = resolution.kind === "render" ? (resolution.params["reference"] ?? null) : null;
  const toutes = useMemo(() => [...pipeline, ...liste, ...archives], [pipeline, liste, archives]);

  /** The file the state holds: the one the last entry effect opened (it may lag the address by a render, `WS-1`). */
  const ouvertHeld = useMemo(
    () =>
      ouvertId === null
        ? null
        : ((ouvertLu?.id === ouvertId ? ouvertLu : null) ??
          [...pipeline, ...liste, ...archives].find((file) => file.id === ouvertId) ??
          null),
    [ouvertId, ouvertLu, pipeline, liste, archives],
  );
  // A file's address is its reference; the id the doors take is read from the lists the server sent HER.
  // `CORRIDOR-439`: the file already open under this address stays hers while the lists catch up (a list read can straddle its close).
  const fileOfAddress = useMemo(
    () =>
      routeReference === null
        ? null
        : (toutes.find((file) => file.reference === routeReference) ??
          (ouvertHeld !== null && ouvertHeld.reference === routeReference ? ouvertHeld : null)),
    [routeReference, toutes, ouvertHeld],
  );
  /**
   * `WS-1` (`WORKSPACE-STALE-READS`): **the file drawn belongs to the address.** On the dossier address the open file IS the address's file, in the
   * render itself, so no handler can hold an id the address does not name (the entry effect used to move `ouvertId` a render later). Her own read,
   * fresher than the list's row, stands for it when it is hers; off the dossier address the state's file is what it was.
   */
  const ouvert = useMemo(
    () =>
      routeScreen !== "dossier"
        ? ouvertHeld
        : fileOfAddress === null
          ? null
          : ouvertLu?.id === fileOfAddress.id
            ? ouvertLu
            : fileOfAddress,
    [routeScreen, ouvertHeld, fileOfAddress, ouvertLu],
  );
  /** `WS-1`: the file the page draws NOW, for the reads in flight to compare with: an answer for any other file is dropped, never drawn. */
  const drawnFileId = useRef<string | null>(null);
  drawnFileId.current = ouvert?.id ?? null;
  // `DECISION-ANALYSIS-SCOPE`: the file the ADDRESS names, whichever screen it is: an analysis answer for any other file is dropped (latest wins).
  const addressFileId = useRef<string | null>(null);
  addressFileId.current = fileOfAddress?.id ?? null;
  /** The analysis read for `fileId`, or nothing yet: another file's read is never handed on, whatever order the answers come in. */
  const analysisOf = (
    fileId: string | null,
  ): {
    analysis: AnalysisView | null;
    analysisSentence: string | null;
    analysisLoaded: boolean;
    analysisUnavailable: boolean;
  } =>
    analysisRead.fileId !== null && analysisRead.fileId === fileId
      ? {
          analysis: analysisRead.analysis,
          analysisSentence: analysisRead.sentence,
          analysisLoaded: true,
          analysisUnavailable: analysisRead.unavailable,
        }
      : { analysis: null, analysisSentence: null, analysisLoaded: false, analysisUnavailable: false };
  const { analysis, analysisSentence, analysisLoaded, analysisUnavailable } = analysisOf(ouvert?.id ?? null);
  const decisionAnalysis = analysisOf(fileOfAddress?.id ?? null);
  // `WS-2`: what is drawn of the file's reads is the record written for THIS file; for any other file, nothing yet.
  const {
    points,
    progression,
    composer,
    offerRead,
    offerTransmittedAt,
    banksReached,
    completion,
    refusalRead,
    refusalReasons,
    lostRead,
    signatureRead,
    reportRead,
    conditionsRead,
    disbursementRead,
    laterObligations,
    ordreRead,
    offerFailed,
  } = reads.fileId !== null && reads.fileId === ouvert?.id ? reads : NO_READS;

  // `S3`: opening a file (or another) starts on its composer again.
  useEffect(() => {
    setBackToReview(null);
  }, [ouvertId]);

  const charger = useCallback(async (): Promise<void> => {
    try {
      const [mine, live, closed] = await Promise.all([
        api.request<unknown>("/api/v1/bank/pipeline", "get"),
        api.request<unknown>("/api/v1/bank/files", "get"),
        api.request<unknown>("/api/v1/bank/files", "get", { query: { archive: "true" } }),
      ]);
      setPipeline(fichiersFrom(mine));
      setListe(fichiersFrom(live));
      setArchives(fichiersFrom(closed));
      setListsFailed(false);
      setListsLoaded(true);
    } catch (failure) {
      // `PIPELINE-STALE-LIST`: a failed read says so (the Pipeline draws « Réessayer »), never the copy it held as if it were current (A47).
      setListsFailed(true);
      throw failure;
    }
  }, [api]);
  const [listsRetry, setListsRetry] = useState(0);
  const retryLists = useCallback((): void => {
    setListsFailed(false);
    setListsRetry((n) => n + 1);
  }, []);

  /** `U-2`, `M3`: the analysis's own read, beside the file's , a door that answers null with R81's sentence. */
  const lireAnalyse = useCallback(
    // `DECISION-ANALYSIS-SCOPE`: every caller is guarded; by default a read is wanted only while the address still names its file.
    async (
      fileId: string,
      stillWanted: () => boolean = () => addressFileId.current === fileId,
    ): Promise<void> => {
      let unavailable = false;
      const read = await api
        .request<unknown>("/api/v1/bank/files/{id}/underwriting", "get", { params: { id: fileId } })
        .catch(() => {
          unavailable = true;
          return null;
        });
      if (!stillWanted()) return;
      setAnalysisRead({
        fileId,
        analysis: analysisFrom(read),
        sentence: analysisSentenceFrom(read),
        unavailable,
      });
    },
    [api],
  );

  /** The file's own read: the row, and its points and progress once it is in review. */
  const lireDossierLu = useCallback(
    async (fileId: string): Promise<void> => {
      // `WS-1`: latest wins , an answer for a file that is no longer the one drawn is dropped at every await, so it never lands over the file that is.
      const wanted = (): boolean => drawnFileId.current === fileId;
      const answer = await api.request<unknown>("/api/v1/bank/files/{id}", "get", { params: { id: fileId } });
      if (!wanted()) return;
      const file = fichierFrom(answer);
      if (file !== null) setOuvertLu(file);
      putReads(fileId, { points: pointsFrom(answer), progression: progressionFrom(answer) });
      await lireAnalyse(fileId, wanted);
      if (!wanted()) return;
      // `L-1`, `M3`: at `revue` the composer's door says whether the sixteen are
      // handled; the screen opens the composer on that word, never on a count of its own.
      // `L-1b`: the same read carries the chef's decision, the open instructions
      // and the send's stamps , the validation and offer panels read it too.
      // `L-2`, `M3`: the offer read stands after the client's answer too (F5).
      if (
        file?.stage === "revue" ||
        file?.stage === "retour" ||
        file?.stage === "validation" ||
        file?.stage === "offre" ||
        file?.stage === "comite" ||
        file?.stage === "signature" ||
        file?.stage === "conditions" ||
        file?.stage === "perdu"
      ) {
        // `SYNTHESE-READ-FAILURE` (B): a failed offer read is CAUGHT, so the file's other reads below still run and the failure is a notice,
        // never a throw that ends the read in silence; and a 404 of the OFFER door is no verdict on the FILE (`lireDossier`'s `ApiError` check).
        const offerResult = await api
          .request<unknown>("/api/v1/bank/files/{id}/offer", "get", { params: { id: fileId } })
          .then(
            (value) => ({ failed: false as const, value }),
            () => ({ failed: true as const, value: null }),
          );
        if (!wanted()) return;
        const offer = offerResult.value;
        const facts = composerFactsFrom(offer);
        setSyntheseFacts({ fileId, facts });
        const reached = (offer as { banksReached?: unknown } | null)?.banksReached;
        putReads(fileId, {
          composer:
            facts === null || file.stage !== "revue"
              ? null
              : {
                  facts,
                  stored: storedOfferFrom(offer),
                  reviewComplete: reviewCompleteFrom(offer),
                  ownFile: ownFileFrom(offer),
                },
          offerRead: fileOfferFrom(offer),
          offerTransmittedAt: transmittedAtFrom(offer),
          banksReached: typeof reached === "number" ? reached : null,
          offerFailed: offerResult.failed,
          // Round `L-3`, `M1`: the standing return and the pièces the request may name, the file's own read.
          completion: completionFrom(answer),
        });
      } else {
        putReads(fileId, { composer: null, offerRead: null });
        // `A-1b-2a`: the stages the offer panels do not read still hand the synthesis its facts; a refusal is a dash, never a number.
        // `SYNTHESE-READ-FAILURE`: a read that FAILS is told apart from one that answers with nothing readable, and from one that is late.
        let offerFailedNow = false;
        const offerRead = await api
          .request<unknown>("/api/v1/bank/files/{id}/offer", "get", { params: { id: fileId } })
          .catch(() => {
            offerFailedNow = true;
            return null;
          });
        if (!wanted()) return;
        const facts = composerFactsFrom(offerRead);
        setSyntheseFacts({ fileId, facts });
        // `ODF-3`: « Le parcours » reads the offer's own send stamp at the stages the offer panels do not.
        putReads(fileId, { offerTransmittedAt: transmittedAtFrom(offerRead), offerFailed: offerFailedNow });
      }
      // Round `L-3b`, `M1`: the refusal on the row (the `refuse` state's banner , found at the shooting:
      // read inside the open stages' branch, the banner printed a dash) and the closed list for the modal.
      putReads(fileId, {
        refusalRead: refusalFrom(answer),
        refusalReasons: reasonsFrom(answer),
        lostRead: lostFrom(answer),
        signatureRead: signatureFrom(answer),
      });
      // Lane E, M2: the report of the submitted file, by the bank's own door; none stands, the panel says so.
      const rapport = await api
        .request<unknown>("/api/v1/bank/files/{id}/rapport", "get", { params: { id: fileId } })
        .catch(() => null);
      if (!wanted()) return;
      putReads(fileId, {
        reportRead: reportFrom(rapport),
        conditionsRead: conditionsFrom(answer),
        disbursementRead: disbursementFrom(answer),
        laterObligations: laterObligationsFrom(answer),
      });
      // `T-2`: the ordre's own read, at the conditions and once décaissé.
      if (file?.stage === "conditions" || file?.stage === "decaisse") {
        const ordre = await api
          .request<unknown>("/api/v1/bank/files/{id}/disbursement", "get", { params: { id: fileId } })
          .catch(() => null);
        if (!wanted()) return;
        putReads(fileId, { ordreRead: ordreFrom(ordre) });
      } else {
        putReads(fileId, { ordreRead: null });
      }
    },
    [api, lireAnalyse, putReads],
  );

  const lireDossier = useCallback(
    async (fileId: string): Promise<void> => {
      // `ODF-1`: the file is read first and the ordre's own read after; until it lands the décaissé card says so.
      setOrdreEnLecture(true);
      try {
        await lireDossierLu(fileId);
        setRefusedFileId((current) => (current === fileId ? null : current));
      } catch (failure) {
        // The file door's own answer is the verdict: a 404 for this analyst, and nothing else, makes the file « introuvable ».
        if (failure instanceof ApiError && failure.status === 404) setRefusedFileId(fileId);
        throw failure;
      } finally {
        setOrdreEnLecture(false);
      }
    },
    [lireDossierLu],
  );

  /**
   * `ODF-1`: the funds act's moment. The file moves to `décaissé` by an event a moment after the door answers, so the page
   * says so (`saving`, `waiting`, `late`) and re-reads the file itself at +1 s and +3 s, through `lireDossier`; it stops on
   * `décaissé`, on another file and on unmount (`routes/funds-moment.ts`).
   */
  /** `CORRIDOR-439`: the three lists read again, a failed read leaving them as they were (the file's own read is the authority, never their absence). */
  const refreshLists = useCallback(async (): Promise<void> => {
    try {
      await charger();
    } catch {
      // The lists keep what they held.
    }
  }, [charger]);
  const rereadFunds = useCallback(
    async (fileId: string): Promise<void> => {
      // The lists too, so the close lands in ONE coherent read.
      await refreshLists();
      await lireDossier(fileId);
    },
    [refreshLists, lireDossier],
  );
  // The open file reaching `décaissé` is the file moving from the active list to the archive: the lists are read once more, whatever read saw it (the funds
  // moment stops on `décaissé`, so its own re-reads cannot be the only way the lists catch up).
  const stageSeen = useRef<{ readonly id: string; readonly stage: string } | null>(null);
  useEffect(() => {
    const before = stageSeen.current;
    stageSeen.current = ouvert === null ? null : { id: ouvert.id, stage: ouvert.stage };
    if (
      ouvert !== null &&
      before !== null &&
      before.id === ouvert.id &&
      before.stage !== "decaisse" &&
      ouvert.stage === "decaisse"
    ) {
      void refreshLists();
    }
  }, [ouvert, refreshLists]);
  const fonds = useFundsMoment({
    fileId: ouvert?.id ?? null,
    stage: ouvert?.stage ?? null,
    reread: rereadFunds,
  });

  /**
   * `T-1`, `M1` (WF-BNK-15 step 2): « Marquer signé » , the scan filed by the documents' own
   * ticket under the signature document's id (a signature is a document with provenance), then
   * the door that records it; the file re-read after.
   */
  const markSigned = useCallback(
    async (fileId: string, documentId: string, scan: File): Promise<void> => {
      setBusy(true);
      setRefusal(null);
      try {
        const ticket = await api.request<{ id: string; url: string; headers: Record<string, string> }>(
          "/api/v1/documents/{ownerType}/{ownerId}/upload-ticket",
          "post",
          {
            params: { ownerType: "bankSignatureDocument", ownerId: documentId },
            body: { name: scan.name, mimeType: scan.type, sizeBytes: scan.size, source: "agence" },
          },
        );
        const put = await fetch(ticket.url, { method: "PUT", headers: ticket.headers, body: scan });
        if (!put.ok) throw new Error(t("common.error.connexionEchouee"));
        const confirmed = await api.request<{ documentId: string }>(
          "/api/v1/documents/{ownerType}/{ownerId}/confirm",
          "post",
          {
            params: { ownerType: "bankSignatureDocument", ownerId: documentId },
            body: { ticketId: ticket.id },
          },
        );
        await api.request<unknown>("/api/v1/bank/files/{id}/documents/{docId}/signed", "post", {
          params: { id: fileId, docId: documentId },
          body: { documentId: confirmed.documentId },
        });
        await lireDossier(fileId);
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    },
    [api, lireDossier],
  );

  /** `T-1`, `M1`: the signature as the file's read carries it (the pack, the appointment, the scans). */
  /** Lane E, M2: the report of the open file (paper, Hakili's sheet, provenance, validity). */
  /** `T-1b`, `M1`: the conditions, the obligations after and the disbursement instruction, the file's read. */
  // `T-2`: the ordre's read at the conditions and after the funds.
  // Round `L-5`: « Politique » and « Équipe », each read by its own door when opened, re-read after each act.
  const [policyRead, setPolicyRead] = useState<PolicyRead | null>(null);
  const [teamRead, setTeamRead] = useState<TeamRead | null>(null);
  const [teamRefusal, setTeamRefusal] = useState<{ userId: string; message: string } | null>(null);
  const [teamFilter, setTeamFilter] = useState<string | null>(null);
  const [teamFailure, setTeamFailure] = useState<string | null>(null);
  /** `EQUIPE-GESTURES`, round 2: the journal of the moves (its own door) and the toast after a gesture the door accepted. */
  const [teamJournal, setTeamJournal] = useState<TeamJournal | null>(null);
  const [teamNotice, setTeamNotice] = useState<string | null>(null);
  // `KIT-TOAST-D2` part 2: each notice goes by itself after D2's 4200 ms (a presentation timer, not a business rule).
  useNoticeTimer(committeeNotice, () => {
    setCommitteeNotice(null);
  });
  useNoticeTimer(policyNotice, () => {
    setPolicyNotice(null);
  });
  useNoticeTimer(teamNotice, () => {
    setTeamNotice(null);
  });

  /**
   * `U-4`, `M2` (WF-BNK-18): « Interroger Hakili » , the panel's own state. The thread is never
   * held from a render: it is RE-READ by its own door after every question, so what the screen
   * shows is what the trail carries, failures included (A47).
   */
  const [thread, setThread] = useState<readonly QuestionExchangeView[]>([]);
  const [questionRefusal, setQuestionRefusal] = useState<string | null>(null);
  /** `ASK-WIDE` (D-1028-4): the wide conversation is open; the question out now; the last one asked (a refusal stands under it). */
  const [wide, setWide] = useState(false);
  const [asking, setAsking] = useState<string | null>(null);
  /** `ASK-WIDE` strip: the pièce (or the report) the viewer stands on, over the panel; one opening, one asking of its door. */
  const [stripView, setStripView] = useState<{
    readonly title: string;
    readonly opening: number;
    /** The conversation's chip that opened it: the focus goes back to it (frame 180). */
    readonly returnTo: string;
    readonly door: () => Promise<readonly DocumentFileView[] | null>;
  } | null>(null);
  const [lastAsked, setLastAsked] = useState<string | null>(null);
  /** Under 1100 px the wide conversation is modal (D2-design §12). */
  const [narrow, setNarrow] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia("(max-width: 1099px)").matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(max-width: 1099px)");
    const follow = (): void => {
      setNarrow(query.matches);
    };
    query.addEventListener("change", follow);
    return () => {
      query.removeEventListener("change", follow);
    };
  }, []);

  /**
   * `U-4`, `M3` (`U-3`'s booking 7): the bell's rows, by HER own door. The count the shell prints
   * is derived from these rows and never carried beside them, so a row marked read lowers it by
   * the same arithmetic on every screen.
   */
  /** `MARK-ALL-READ`: the number the door served (the list's, then the mark-all door's), never a count of the rows below. */
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState<
    readonly {
      id: string;
      title: string;
      body: string;
      linkPath: string | null;
      readAt: string | null;
      createdAt: string;
      kind: string;
      subjectType: string | null;
      subjectId: string | null;
    }[]
  >([]);

  const suivre = useCallback(
    async (call: Promise<unknown>): Promise<void> => {
      // `WS-2`: the act's answer is that file's, whichever file is drawn when it lands.
      const forFile = drawnFileId.current;
      setBusy(true);
      setRefusal(null);
      try {
        const answer = await call;
        if (forFile !== null)
          putReads(forFile, { points: pointsFrom(answer), progression: progressionFrom(answer) });
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    },
    [putReads],
  );

  const loadPolicy = useCallback(async (): Promise<PolicyRead> => {
    const answer = await api.request<PolicyRead>("/api/v1/bank/policy", "get");
    setPolicyRead(answer);
    return answer;
  }, [api]);
  const loadTeam = useCallback(async (): Promise<void> => {
    try {
      // `C-2` (A48): the answer is PARSED with the contract the document declares, never cast; a mismatch is said on the screen.
      const answer = bankTeamAnswerSchema.parse(await api.request<unknown>("/api/v1/bank/team", "get"));
      setTeamRead(answer as TeamRead);
      setTeamFailure(null);
      // The journal is read beside the team, by its own door; a refusal of it does not hide the team (A48: parsed, never cast).
      try {
        setTeamJournal(
          teamJournalSchema.parse(await api.request<unknown>("/api/v1/bank/team/journal", "get")),
        );
      } catch {
        setTeamJournal(null);
      }
    } catch (failure) {
      setTeamFailure(t("bank.equipe.lectureImpossible"));
      throw failure;
    }
  }, [api]);
  /** `L-5`, `M2`: the chef's acts on his team, each by its door, the refusal kept beside the member. */
  const followTeam = useCallback(
    async (userId: string | null, call: Promise<unknown>): Promise<void> => {
      setBusy(true);
      setTeamRefusal(null);
      try {
        await call;
        await loadTeam();
      } catch (failure) {
        const message =
          (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");
        setTeamRefusal({ userId: userId ?? "", message });
      } finally {
        setBusy(false);
      }
    },
    [loadTeam],
  );

  /** The chef's act: the refusal in the door's words, the busy flag. */
  const followDecision = useCallback(async (call: Promise<unknown>): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    try {
      await call;
    } catch (failure) {
      setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      throw failure;
    } finally {
      setBusy(false);
    }
  }, []);

  /**
   * WF-BNK-08, D-1026-AA-4: the analyst's refusal by her own door, from the review's footer and from the nouveau's: one handler, one door;
   * the file is at `refuse` on the re-read.
   */
  const refuseFile = (fileId: string, reasonCode: string): void => {
    void followDecision(
      api.request<unknown>("/api/v1/bank/files/{id}/refuse", "post", {
        params: { id: fileId },
        body: { reasonCode },
      }),
    ).then(async () => {
      await charger();
      await lireDossier(fileId);
    });
  };

  /** `L-1b`, `M4`: the chef's queue, by his own door. */
  const loadDecisions = useCallback(async (): Promise<void> => {
    const answer = await api.request<unknown>("/api/v1/bank/decisions", "get");
    setQueue(queueFrom(answer));
  }, [api]);

  /** `U-4`, `M3`: her own rows, read on every mount and after her act of reading one. */
  const loadNotifications = useCallback(async (): Promise<void> => {
    const answer = await api.request<NotificationsAnswer>("/api/v1/me/notifications", "get");
    setUnread(answer.unread);
    setNotifications(
      answer.notifications.map((row) => ({
        id: row.id,
        title: row.title,
        body: row.body,
        linkPath: row.linkPath,
        readAt: row.readAt,
        createdAt: row.createdAt,
        kind: row.kind,
        subjectType: row.subjectType,
        subjectId: row.subjectId,
      })),
    );
  }, [api]);

  /** `U-4`, `M2`: her thread, by her own door , read when the panel opens and after each question. */
  const loadThread = useCallback(
    async (fileId: string): Promise<void> => {
      const answer = await api.request<unknown>("/api/v1/bank/files/{id}/questions", "get", {
        params: { id: fileId },
      });
      setThread(threadFrom(answer));
    },
    [api],
  );

  /**
   * Her question, through the door that opens the platform's OWN read doors with HER token. The
   * refusal stays in the panel in the product's words; the thread is re-read either way, because a
   * question that failed is still on the trail and she is owed the sight of it.
   */
  const askQuestion = useCallback(
    async (fileId: string, question: string): Promise<void> => {
      setBusy(true);
      setQuestionRefusal(null);
      setAsking(question);
      setLastAsked(question);
      try {
        await api.request<unknown>("/api/v1/bank/files/{id}/questions", "post", {
          params: { id: fileId },
          body: { question },
        });
      } catch (failure) {
        setQuestionRefusal(
          (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
        );
      } finally {
        setBusy(false);
        setAsking(null);
      }
      await loadThread(fileId).catch(() => {
        // The panel keeps what it had; the refusal above says why nothing new arrived.
      });
    },
    [api, loadThread],
  );

  const readDecision = useCallback(
    async (fileId: string): Promise<void> => {
      const answer = await api.request<unknown>("/api/v1/bank/decisions/{id}", "get", {
        params: { id: fileId },
      });
      const read = readDecisionAnswer(answer);
      if (!read.ok) {
        setDecisionFile(null);
        setDecisionBroken(fileId);
        return;
      }
      setDecisionBroken(null);
      setDecisionFile(read.file);
      const [file, offer] = await Promise.all([
        api.request<unknown>("/api/v1/bank/files/{id}", "get", { params: { id: fileId } }).catch(() => null),
        api
          .request<unknown>("/api/v1/bank/files/{id}/offer", "get", { params: { id: fileId } })
          .catch(() => null),
      ]);
      setDecisionHead({
        fileId,
        vehicleYear: composerFactsFrom(offer)?.vehicleYear ?? null,
        requestedMonths: file === null ? null : (fichierFrom(file)?.durationMonths ?? null),
      });
    },
    [api],
  );

  const readCommittee = useCallback(
    async (fileId: string): Promise<void> => {
      committeeReadSeq.current += 1;
      const seq = committeeReadSeq.current;
      const answer = await api.request<unknown>("/api/v1/bank/decisions/{id}", "get", {
        params: { id: fileId },
      });
      if (seq !== committeeReadSeq.current) return;
      const read = readDecisionAnswer(answer);
      setDecisionBroken(read.ok ? null : fileId);
      setCommitteeRead(read.ok ? { fileId, file: read.file } : null);
    },
    [api],
  );

  useEffect(() => {
    if (!authentifie) return;
    void loadNotifications().catch(() => {
      // The bell stays empty and says so in the drawing's words; nothing is invented for it.
    });
  }, [authentifie, loadNotifications]);

  useEffect(() => {
    if (!authentifie) return;
    void charger().catch(() => {
      // The lists stay empty and the Pipeline says so in the drawing's words (frame 04), with « Réessayer ».
      setListsFailed(true);
    });
  }, [authentifie, charger, listsRetry]);

  // `PIPELINE-STALE-LIST`: entering « Pipeline » or « Dossiers » reads the lists again, once per entry (A47: a read of mutable state happens when it is
  // asked). The sign-in's own read serves the first screen; moving between the two list screens is an entry too.
  const listScreenSeen = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!authentifie) {
      listScreenSeen.current = undefined;
      return;
    }
    const entered = routeScreen === "pipeline" || routeScreen === "dossiers" ? routeScreen : null;
    const before = listScreenSeen.current;
    listScreenSeen.current = entered;
    if (before === undefined || entered === null || entered === before) return;
    void charger().catch(() => undefined);
  }, [authentifie, routeScreen, charger]);

  // The root names no screen: it is the persona's landing, put in place of the root (no entry left to come back to).
  useEffect(() => {
    if (!authentifie || roleNow === null || (path !== "/" && path !== "")) return;
    globalThis.history.replaceState(null, "", landingOf(routesPersona));
    globalThis.dispatchEvent(new PopStateEvent("popstate"));
  }, [authentifie, roleNow, path, routesPersona]);

  /**
   * `POLICY-FIRST` Screen A (D2 `screens2.js:246`: « STATE.nopol ? "#/c/politique" : "#/c/decisions" »): **the chef of a bank with no policy
   * lands on the Politique, where the first-policy form is.** Only at the moment of signing in (never on a reload, which keeps the typed
   * address), only from his own landing, and only on the door's own answer (`GET /api/v1/bank/policy` reading `policy: null`): a failed
   * read sends him nowhere. The analyst keeps her Pipeline.
   */
  const policyLandingArmed = useRef(false);
  useEffect(() => {
    if (session.status === "anonymous") policyLandingArmed.current = true;
  }, [session.status]);
  useEffect(() => {
    if (!authentifie || !policyLandingArmed.current) return;
    // The root is put in place of the landing by the effect above: wait for it.
    if (path === "/" || path === "") return;
    policyLandingArmed.current = false;
    if (routesPersona !== "chef" || path !== landingOf(routesPersona)) return;
    void loadPolicy()
      .then((answer) => {
        if (answer.policy === null) naviguer(addressOf("politique"));
      })
      .catch(() => undefined);
  }, [authentifie, routesPersona, path, loadPolicy, naviguer]);

  /**
   * `AUTH-LANDING` (staging, 26 October 2026): **signing in returns only to an address her role may open, else to her home.**
   * The sign-in form is drawn at ANY address, so the address the person signed in at is whatever the bar held: after a reset it
   * was the reset link's (« Cette page n'existe pas »), and a chef signing in after an analyst met the analyst's list. At the
   * moment of signing in (anonymous to authenticated, never on a reload, which keeps the typed address and its refusal) an
   * address that does not render for her role, a public auth route among them, is put in place of her landing.
   */
  const wasAnonymous = useRef(false);
  useEffect(() => {
    if (session.status === "anonymous") {
      wasAnonymous.current = true;
      return;
    }
    if (session.status !== "authenticated" || !wasAnonymous.current) return;
    wasAnonymous.current = false;
    if (path === "/" || path === "" || resolution.kind === "render") return;
    globalThis.history.replaceState(null, "", landingOf(routesPersona));
    globalThis.dispatchEvent(new PopStateEvent("popstate"));
  }, [session.status, path, resolution, routesPersona]);

  // `POLICY-FIRST` Screen B: whether the bank has a policy is the door's own fact, read as soon as the person is signed in (the chef and the
  // analyst may both read it), so the banner does not wait for the Politique to be opened. A failed read draws no banner: nothing is guessed.
  useEffect(() => {
    if (!authentifie) return;
    void loadPolicy().catch(() => undefined);
  }, [authentifie, loadPolicy]);

  // Entering a screen reads what it shows, as the rail used to say it (`onScreen`).
  useEffect(() => {
    if (!authentifie) return;
    if (routeScreen === "decisions") {
      setDecisionId(null);
      void loadDecisions().catch(() => undefined);
    } else if (routeScreen === "politique") {
      setRefusal(null);
      setPolicyNotice(null);
      setPolicySaved(null);
      void loadPolicy().catch(() => undefined);
    } else if (routeScreen === "equipe") {
      setTeamRefusal(null);
      void loadTeam().catch(() => undefined);
    } else if (routeScreen === "pipeline" || routeScreen === "dossiers") {
      setOuvertId(null);
      // Frame 66: the chef's pipeline names the analyst on each row, from the team's own read.
      if (routeScreen === "pipeline" && routesPersona === "chef") void loadTeam().catch(() => undefined);
    }
  }, [authentifie, routeScreen, routesPersona, loadDecisions, loadPolicy, loadTeam]);

  // `DECISION-STALE-ID`: the decision screen is drawn only for the file the address names; its reference is the address's.
  const decisionShown = decisionFileOfAddress(
    decisionFile,
    routeScreen === "decision" ? routeReference : null,
  );
  useEffect(() => {
    if (!authentifie || routeScreen !== "dossier" || fileOfAddress === null || ouvertId === fileOfAddress.id)
      return;
    setOuvertId(fileOfAddress.id);
    setOuvertLu(null);
    setThread([]);
    setQuestionRefusal(null);
    setWide(false);
    setStripView(null);
    void loadThread(fileOfAddress.id).catch(() => undefined);
    void lireDossier(fileOfAddress.id).catch(() => undefined);
  }, [authentifie, routeScreen, fileOfAddress, ouvertId, lireDossier, loadThread]);
  useEffect(() => {
    if (
      !authentifie ||
      routeScreen !== "decision" ||
      fileOfAddress === null ||
      decisionId === fileOfAddress.id
    )
      return;
    setDecisionId(fileOfAddress.id);
    setDecisionFile(null);
    setRefusal(null);
    void readDecision(fileOfAddress.id).catch(() => undefined);
    void lireAnalyse(fileOfAddress.id).catch(() => undefined);
  }, [authentifie, routeScreen, fileOfAddress, decisionId, readDecision, lireAnalyse]);

  const committeeOf = routeScreen === "comite" && fileOfAddress !== null ? fileOfAddress.id : null;
  // `COMITE-STALE-FILE`: leaving the screen forgets its read, in the same render (before anything is drawn from it), so the next opening finds none.
  if (committeeOf === null && committeeRead !== null) setCommitteeRead(null);
  // Drawn only from a read made for the file the address names now.
  const committeeFile =
    committeeRead !== null && committeeRead.fileId === committeeOf ? committeeRead.file : null;
  useEffect(() => {
    if (!authentifie || committeeOf === null) return;
    setCommitteeRead(null);
    setCommitteeNotice(null);
    setRefusal(null);
    void readCommittee(committeeOf).catch(() => undefined);
  }, [authentifie, committeeOf, readCommittee]);

  const ouvrir = (file: FichierBanque): void => {
    naviguer(dossierAddress(file.reference));
  };

  if (jeton !== null) {
    return (
      <Invitation
        token={jeton}
        lire={(token) => api.request<unknown>("/api/v1/invitations/{token}", "get", { params: { token } })}
        accepter={async (token, password) => {
          await api.request<unknown>("/api/v1/invitations/{token}/accept", "post", {
            params: { token },
            body: { password },
          });
        }}
      />
    );
  }

  if (lienReinit !== null) {
    return (
      <NouveauMotDePasse
        token={lienReinit.token}
        check={lireLienReinit}
        reset={async (token, password) => {
          await api.request<unknown>("/api/v1/auth/password/reset", "post", { body: { token, password } });
        }}
        onDone={() => {
          setReinitFaite(true);
          quitterReinit();
        }}
        onNewLink={() => {
          quitterReinit();
          setOubli(true);
        }}
        onBack={quitterReinit}
      />
    );
  }

  if (session.status === "pending") {
    return <p className="p-6">{t("bank.session.chargement")}</p>;
  }

  if (session.status !== "authenticated" && oubli) {
    return (
      <MotDePasseOublie
        request={async (email) => {
          await api.request<unknown>("/api/v1/auth/password/reset-request", "post", { body: { email } });
        }}
        onBack={() => {
          setOubli(false);
        }}
      />
    );
  }

  if (session.status !== "authenticated") {
    return (
      <Connexion
        onForgot={() => {
          setOubli(true);
        }}
        resetDone={reinitFaite}
        refusal={session.refusal}
        // `FX-3`, `M3` (`S2-2`): the second factor's own state, so the field appears where the door asked
        // for a code and nowhere else.
        codeRequired={session.challenged === true}
        // `C-1` (R-18): a session that expired says so and keeps the e-mail.
        expired={expired}
        pending={false}
        onSubmit={(credentials) => {
          void connecter(credentials);
        }}
      />
    );
  }

  const roleFromRole = personaDe(session.identity.role);
  if (roleFromRole === null) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 px-6">
        <h1 className="text-xl font-semibold" data-testid="bank-bleed">
          {t("bank.bleed.titre")}
        </h1>
        <p className="text-sm text-gray-600">{t("bank.bleed.explication")}</p>
        <button
          type="button"
          className="self-start rounded-xl bg-indigo-600 px-3 py-2 text-white"
          onClick={() => {
            void deconnecter();
          }}
          data-testid="bank-bleed-logout"
        >
          {t("bank.session.deconnexion")}
        </button>
      </main>
    );
  }
  const demo = { serveur: session.identity.flags["demo"] === true, productionBuild: import.meta.env.PROD };
  const persona: Persona = personaDemo ?? roleFromRole;
  // The landing is the first of the persona's list (03 §3.0): the chef's « Décisions », the analyst's « Pipeline ».
  const shown: BankScreen =
    routeScreen === null ? (persona === "chef" ? "decisions" : "pipeline") : shellScreenOf(routeScreen);
  const all = [...liste, ...archives];
  /**
   * `D-1020-4`: the page an address that is not hers lands on. A known address outside her whitelist names whose it
   * is; an unknown one says so; a file that is not in her lists says that. The root is the landing, in place.
   */
  const motifPage: MotifPage | null =
    path === "/" || path === ""
      ? null
      : resolution.kind === "redirect"
        ? resolution.reason === "unknown_path"
          ? "introuvable"
          : ownerOf(find(tableFor(persona), path)?.route.screen ?? "") === "analyste"
            ? "reserve-analyste"
            : "reserve-chef"
        : listsLoaded &&
            (routeScreen === "dossier" || routeScreen === "decision" || routeScreen === "comite") &&
            (fileOfAddress === null || (refusedFileId !== null && fileOfAddress.id === refusedFileId))
          ? "dossier-introuvable"
          : null;
  const landing = landingOf(persona);
  // What is drawn: nothing of a screen while the address is refused or names no screen.
  const vue: BankScreen | null = motifPage === null ? shown : null;
  /**
   * `F-2` (D2 §5): the underwriter's bay, beside every dossier screen , the workspace in its stage's context, the chef's
   * decision in its own. It binds to the analysis door's answer and to nothing else; a list screen has no bay.
   */
  const bayNode =
    vue === "workspace" && ouvert !== null ? (
      <UnderwriterBay
        context={bayContextFor(ouvert.stage, ouvert.stage === "revue" && composer?.reviewComplete === true)}
        watch={{ reportValidUntil: disbursementRead?.reportValidity?.validUntil ?? null }}
        analysis={analysis}
        loaded={analysisLoaded}
        unavailable={analysisUnavailable}
        onRetry={() => {
          void lireAnalyse(ouvert.id);
        }}
        sentence={analysisSentence}
        busy={busy}
        flaggedPoints={points.filter((one) => one.flagged).length}
        composerFlagging={composerFlagging}
        onUseProposal={() => {
          setProposalTaken((n) => n + 1);
        }}
        onPolitique={() => {
          // D2 frame 35: « Voir la politique » for the analyst too, opening her read-only copy (`ECRANS`, `readOnly={persona !== "chef"}`).
          naviguer("/politique");
        }}
        onSecondLook={async (body) => {
          // `OD-37` round 10b (§9.4): her second look by her own door, her name from her session; the
          // refusal is the door's sentence and is printed inside the form.
          await api.request<unknown>("/api/v1/bank/files/{id}/underwriting/second-look", "post", {
            params: { id: ouvert.id },
            body,
          });
          await lireAnalyse(ouvert.id);
        }}
        question={
          <AskEntry
            count={exchangesCounted(thread).length}
            open={wide}
            onOpen={() => {
              setWide(true);
            }}
          />
        }
      />
    ) : vue === "decision" &&
      decisionId !== null &&
      decisionId === fileOfAddress?.id &&
      !(decisionShown === null && decisionBroken === decisionId) ? (
      // `DECISION-ANALYSIS-SCOPE`: drawn for the file the address names, and only from the analysis read for THAT file.
      // `PARSER-GAPCOUNT` (the designer's review): the bay is withheld with the page when the decision's answer breaks its contract; no half-read page.
      <UnderwriterBay
        context="decision"
        derogation={(decisionShown?.gaps.length ?? 0) > 0}
        analysis={decisionAnalysis.analysis}
        loaded={decisionAnalysis.analysisLoaded}
        unavailable={decisionAnalysis.analysisUnavailable}
        onRetry={() => {
          void lireAnalyse(decisionId);
        }}
        sentence={decisionAnalysis.analysisSentence}
        busy={busy}
        {...(persona === "chef"
          ? {
              onPolitique: () => {
                naviguer("/politique");
              },
            }
          : {})}
      />
    ) : undefined;

  return (
    <DemandeFonctionnaliteProvider envoyer={(demande) => submitFeatureRequest(demande)}>
      <BankShell
        persona={persona}
        fullName={session.identity.fullName}
        organisation={session.identity.organisation}
        screen={shown}
        {...(policyRead !== null && policyRead.policy === null
          ? {
              policyMissing: {
                onGo: () => {
                  naviguer(addressOf("politique"));
                },
              },
            }
          : {})}
        notifications={{
          rows: notifications,
          unread,
          onReadAll: () => {
            void api
              .request<ReadAllAnswer>("/api/v1/me/notifications/read", "post")
              .then((served) => {
                // The door re-derived the badge in its own transaction: the panel takes that number and sets no `readAt` itself.
                setUnread(served.unread);
                return loadNotifications();
              })
              .catch(() => {
                // A refused door, or a reload that failed: the rows keep their state, and the number is the last one served.
              });
          },
          onRead: (id: string) => {
            void api
              .request<ReadMarkAnswer>("/api/v1/me/notifications/{id}/read", "post", { params: { id } })
              .then(loadNotifications)
              .catch(() => {
                // Her row keeps its state; a read that did not happen is never drawn as one.
              });
          },
          onOpen: (linkPath: string | null) => {
            // The row's own link, as the notification carries it , a file of hers opens where it lives.
            const fileId =
              linkPath?.startsWith("/bank/files/") === true ? linkPath.slice("/bank/files/".length) : null;
            if (fileId === null) return;
            const file = toutes.find((one) => one.id === fileId);
            if (file !== undefined) naviguer(dossierAddress(file.reference));
          },
        }}
        onScreen={(next) => {
          if (
            next !== "pipeline" &&
            next !== "dossiers" &&
            next !== "decisions" &&
            next !== "equipe" &&
            next !== "politique"
          )
            return;
          const target = addressOf(next);
          if (target !== path) {
            naviguer(target);
            return;
          }
          // The rail's own screen again: the read is made again, as it always was.
          if (next === "decisions") void loadDecisions().catch(() => undefined);
          if (next === "politique") {
            setPolicyNotice(null);
            setPolicySaved(null);
            void loadPolicy().catch(() => undefined);
          }
          if (next === "equipe") void loadTeam().catch(() => undefined);
        }}
        deconnecter={() => {
          void deconnecter();
        }}
        {...(bayNode === undefined ? {} : { bay: bayNode })}
      >
        {motifPage === null ? null : (
          <PageRefusee
            motif={motifPage}
            retour={t(persona === "chef" ? "bank.adresse.retourDecisions" : "bank.adresse.retourPipeline")}
            onRetour={() => {
              naviguer(landing);
            }}
          />
        )}
        {vue === "decisions" ? (
          <HeadDecisions
            fullName={session.identity.fullName}
            queue={queue}
            lire={loadDecisions}
            onOpen={(fileId) => {
              // The address names the file by its reference; the effect above reads the decision by the id.
              // A row the decisions door drew carries its own reference: the lists answer on their own time, and a click
              // that lands before they do must open the file, not do nothing.
              const reference =
                queue?.pending.find((one) => one.id === fileId)?.reference ??
                toutes.find((one) => one.id === fileId)?.reference;
              if (reference !== undefined) naviguer(decisionAddress(reference));
            }}
            onCommittee={(reference) => {
              naviguer(committeeAddress(reference));
            }}
          />
        ) : null}
        {vue === "comite" && committeeOf !== null ? (
          committeeFile === null ? (
            <DecisionPending
              broken={decisionBroken !== null && decisionBroken === committeeOf}
              onRetry={() => {
                void readCommittee(committeeOf).catch(() => undefined);
              }}
              onBack={() => {
                naviguer("/decisions");
              }}
              reference={routeReference}
              route="comite"
            />
          ) : (
            <Committee
              // A recording empties the form (a new sitting is a new form); a refusal of the door keeps what was typed.
              key={String(committeeFile.committee?.sittings.length ?? 0)}
              file={recordingFileOf(committeeFile)}
              busy={busy}
              refusal={refusal}
              notice={committeeNotice}
              onBack={() => {
                naviguer("/decisions");
              }}
              onRecord={(body: CommitteeBody) => {
                void followDecision(
                  api.request<unknown>("/api/v1/bank/decisions/{id}/committee", "post", {
                    params: { id: committeeOf },
                    body,
                  }),
                )
                  .then(async () => {
                    setCommitteeNotice(noticeOf(body.outcome));
                    await readCommittee(committeeOf);
                    await loadDecisions();
                    // `PIPELINE-STALE-LIST`: the recording moves the file's stage too.
                    await charger().catch(() => undefined);
                  })
                  .catch(() => undefined);
              }}
            />
          )
        ) : null}
        {vue === "decision" && decisionId !== null ? (
          decisionShown === null ? (
            <DecisionPending
              broken={decisionBroken !== null && decisionBroken === decisionId}
              onRetry={() => {
                void readDecision(decisionId).catch(() => undefined);
              }}
              onBack={() => {
                naviguer("/decisions");
              }}
              reference={routeReference}
              route="decision"
            />
          ) : comportementOf(path) ? (
            // `S1-a` COMPORTEMENT DU COMPTE, the chef's page behind the decision's button: the decision's head, the two tabs (« Dossier » is the decision), the behaviour read for THIS file when the page opens.
            <>
              <DecisionHead
                file={decisionShown}
                bankName={session.identity.organisation}
                vehicleYear={decisionHead?.fileId === decisionShown.id ? decisionHead.vehicleYear : null}
                requestedMonths={
                  decisionHead?.fileId === decisionShown.id ? decisionHead.requestedMonths : null
                }
                onBack={() => {
                  naviguer("/decisions");
                }}
              />
              <DossierTabs
                active="comportement"
                onSelect={() => {
                  naviguer(decisionAddress(decisionShown.reference));
                }}
              />
              <BehaviourTab
                fileId={decisionShown.id}
                read={() =>
                  api.request<unknown>("/api/v1/bank/files/{id}/behaviour", "get", {
                    params: { id: decisionShown.id },
                  })
                }
              />
            </>
          ) : (
            <Decision
              onComportement={() => {
                naviguer(decisionComportementAddress(decisionShown.reference));
              }}
              file={decisionShown}
              borrowerInsuranceRatePct={decisionShown.facts.borrowerInsuranceRatePct}
              policyMissing={policyRead !== null && policyRead.policy === null}
              bankName={session.identity.organisation}
              vehicleYear={decisionHead?.fileId === decisionShown.id ? decisionHead.vehicleYear : null}
              requestedMonths={
                decisionHead?.fileId === decisionShown.id ? decisionHead.requestedMonths : null
              }
              busy={busy}
              refusal={refusal}
              onBack={() => {
                naviguer("/decisions");
              }}
              preview={async (ratePct: string) => {
                // `DECISION-STALE-ID`: the target is the matched file's id, refused loudly (outside the try) when it is not the address's.
                const target = decisionTargetId(decisionShown, fileOfAddress?.id ?? null);
                // `AJUSTER-TAUX`: the composer's answer at the rate, by the door; none if the door did not answer.
                try {
                  return ratePreviewFrom(
                    await api.request<unknown>("/api/v1/bank/decisions/{id}/preview", "post", {
                      params: { id: target },
                      body: { ratePct },
                    }),
                  );
                } catch {
                  return null;
                }
              }}
              onDecide={(body: DecisionBody) => {
                // `DECISION-STALE-ID`: the matched file's id, refused loudly when it is not the address's file; nothing is posted.
                const target = decisionTargetId(decisionShown, fileOfAddress?.id ?? null);
                // `DECISION-SEND-REFERENCE`: the reference the screen printed rides with the decision; the door refuses a decision typed against another file.
                // The preview does not send it: it reads only the rate and writes nothing.
                void followDecision(
                  api.request<unknown>("/api/v1/bank/decisions/{id}", "post", {
                    params: { id: target },
                    body: { ...body, seenReference: decisionShown.reference },
                  }),
                )
                  .then(async () => {
                    await readDecision(target);
                    await loadDecisions();
                    // `PIPELINE-STALE-LIST`: a decision moves the file's stage; the lists the Pipeline draws are read again.
                    await charger().catch(() => undefined);
                  })
                  // The refusal, the door's own words, is already drawn by `followDecision`; a 409 is an expected answer here, not an unhandled one.
                  .catch(() => undefined);
              }}
            />
          )
        ) : null}
        {vue === "pipeline" ? (
          <Pipeline
            persona={persona}
            etat={listsFailed ? "erreur" : listsLoaded ? "pret" : "chargement"}
            onRetry={retryLists}
            analystNames={new Map((teamRead?.members ?? []).map((one) => [one.id, one.fullName] as const))}
            selfId={session.identity.id}
            fichiers={
              persona === "chef" ? liste : [...pipeline, ...liste.filter((file) => file.stage === "nouveau")]
            }
            onOuvrir={ouvrir}
            entrusted={notifications
              .filter(
                (row) =>
                  row.kind === ENTRUSTED_KIND &&
                  row.subjectType === "bank_file" &&
                  row.subjectId !== null &&
                  row.readAt === null,
              )
              .map((row) => ({ id: row.id, fileId: row.subjectId ?? "", title: row.title, line: row.body }))}
            onReadEntrusted={(id: string) => {
              void api
                .request<ReadMarkAnswer>("/api/v1/me/notifications/{id}/read", "post", { params: { id } })
                .then(loadNotifications)
                .catch(() => undefined);
            }}
            analystFilter={
              teamFilter === null
                ? null
                : {
                    userId: teamFilter,
                    fullName: filterNameOf(teamRead, teamFilter) ?? DASH_NAME,
                    onClear: () => {
                      setTeamFilter(null);
                    },
                  }
            }
          />
        ) : null}
        {vue === "dossiers" ? (
          <Dossiers
            fichiers={all}
            onOuvrir={ouvrir}
            {...(listsFailed ? { failure: { onRetry: retryLists } } : {})}
          />
        ) : null}
        {policyNotice === "first" ? (
          <ToastStack>
            <Toast>{t("bank.politique.premiereEnregistree")}</Toast>
          </ToastStack>
        ) : null}
        {vue === "politique" ? (
          <Politique
            read={policyRead}
            readOnly={persona !== "chef"}
            {...(persona !== "chef" && policyFrom !== null
              ? {
                  origin: {
                    onOpen: () => {
                      naviguer(dossierAddress(policyFrom));
                    },
                  },
                }
              : {})}
            busy={busy}
            refusal={refusal}
            notice={policyNotice}
            saved={policySaved}
            onSave={(policy: PolicyWrite) => {
              // `POLICY-FIRST` Screen A: the save of a bank that had no policy is its FIRST definition (frame 165): it says so in a toast and
              // takes him to the Décisions, where the dossiers can now be validated (D2 `fp-save`).
              const firstDefinition = policyRead !== null && policyRead.policy === null;
              // The chef's own door, the grid replaced whole; re-read after. What the save says follows the row the DOOR answers
              // (the grid's highest, `KIT-TOAST-D2` 3a): the screen compares no rate itself (D-1020-7).
              setPolicyNotice(null);
              setPolicySaved(null);
              let answered: PolicySaved | null = null;
              void followDecision(
                api.request<unknown>("/api/v1/bank/policy", "put", { body: policy }).then((body) => {
                  const stored = bankPolicyWriteSchema.parse(body);
                  answered = {
                    capacityRowGrade: stored.capacityRowGrade,
                    capacityRatePct: stored.capacityRatePct,
                  };
                }),
              )
                .then(loadPolicy)
                .then(() => {
                  if (answered === null) return;
                  if (firstDefinition) {
                    setPolicyNotice("first");
                    naviguer(addressOf("decisions"));
                    return;
                  }
                  setPolicySaved(answered);
                  setPolicyNotice(answered.capacityRowGrade === "C" ? "capacityC" : "standard");
                })
                .catch(() => undefined);
            }}
          />
        ) : null}
        {vue === "equipe" ? (
          <Equipe
            read={teamRead}
            failure={teamFailure}
            busy={busy}
            refusal={teamRefusal}
            files={liste}
            journal={teamJournal}
            notice={teamNotice}
            gestures={{
              previewReassign: async (fileId) =>
                reassignmentPreviewSchema.parse(
                  await api.request<unknown>("/api/v1/bank/files/{id}/reassign", "get", {
                    params: { id: fileId },
                  }),
                ),
              reassign: async (fileId, toUserId, runId) => {
                const moved = fileMovedSchema.parse(
                  await api.request<unknown>("/api/v1/bank/files/{id}/reassign", "post", {
                    params: { id: fileId },
                    body: { toUserId, ...(runId === null ? {} : { runId }) },
                  }),
                );
                setTeamNotice(
                  fill("bank.equipe.toastReaffecte", {
                    client: liste.find((one) => one.id === fileId)?.clientName ?? moved.reference,
                    vers: moved.toFullName,
                  }),
                );
                await Promise.all([loadTeam(), charger()]).catch(() => undefined);
              },
              previewTransfer: async (userId) =>
                transferPreviewSchema.parse(
                  await api.request<unknown>("/api/v1/bank/team/{userId}/portfolio/transfer", "get", {
                    params: { userId },
                  }),
                ),
              transfer: async (userId) => {
                transferDoneSchema.parse(
                  await api.request<unknown>("/api/v1/bank/team/{userId}/portfolio/transfer", "post", {
                    params: { userId },
                    body: {},
                  }),
                );
                setTeamNotice(t("bank.equipe.toastTransfere"));
                await Promise.all([loadTeam(), charger()]).catch(() => undefined);
              },
            }}
            doors={{
              onAbsence: (userId, returnsOn) => {
                void followTeam(
                  userId,
                  api.request<unknown>("/api/v1/users/{id}/availability", "patch", {
                    params: { id: userId },
                    body: { availability: "absent", returnsOn },
                  }),
                );
              },
              onBack: (userId) => {
                void followTeam(
                  userId,
                  api.request<unknown>("/api/v1/users/{id}/availability", "patch", {
                    params: { id: userId },
                    body: { availability: "actif" },
                  }),
                );
              },
              onRemove: (userId) => {
                void followTeam(
                  userId,
                  api.request<unknown>("/api/v1/users/{id}/deactivate", "post", {
                    params: { id: userId },
                    body: {},
                  }),
                );
              },
              onInvite: (email, fullName, level) => {
                void followTeam(
                  null,
                  api.request<unknown>("/api/v1/users", "post", {
                    body: { fullName, email, role: "analysteCredit", level },
                  }),
                );
              },
              onFiles: (userId) => {
                setTeamFilter(userId);
                naviguer("/pipeline");
              },
            }}
          />
        ) : null}
        {vue === "workspace" && ouvert !== null ? (
          <Workspace
            file={ouvert}
            rapport={<ReportPanel view={reportRead} />}
            lost={lostRead}
            comite={offerRead?.requiresCommittee}
            busy={busy}
            vehicleYear={
              syntheseFacts?.fileId === ouvert.id ? (syntheseFacts.facts?.vehicleYear ?? null) : null
            }
            bankName={session.identity.organisation}
            composing={
              ouvert.stage === "revue" && composer?.reviewComplete === true && backToReview !== ouvert.id
            }
            grade={analysis?.grade ?? null}
            revue={progression === null ? null : { handled: progression.handled, total: progression.total }}
            tabs={{
              // `S1-a` COMPORTEMENT DU COMPTE: the strip once, for every stage; the tab's own address (`comportementOf`), its read made when it opens (A47).
              strip: (
                <DossierTabs
                  active={comportementOf(path) ? "comportement" : "dossier"}
                  onSelect={(tab) => {
                    naviguer(
                      tab === "comportement"
                        ? dossierComportementAddress(ouvert.reference)
                        : dossierAddress(ouvert.reference),
                    );
                  }}
                />
              ),
              active: comportementOf(path) ? "comportement" : "dossier",
              behaviour: (
                <BehaviourTab
                  fileId={ouvert.id}
                  read={() =>
                    api.request<unknown>("/api/v1/bank/files/{id}/behaviour", "get", {
                      params: { id: ouvert.id },
                    })
                  }
                />
              ),
            }}
            onPipeline={() => {
              naviguer("/pipeline");
            }}
            synthesis={
              // D2 (43, 44) draws no « Afficher la synthèse du dossier » at the committee stage.
              ouvert.stage === "comite" ? null : (
                <Synthese
                  key={ouvert.id}
                  file={ouvert}
                  analysis={analysis}
                  // Never another file's facts: they ride with the id they were read for.
                  facts={syntheseFacts?.fileId === ouvert.id ? syntheseFacts.facts : null}
                  offerFailed={offerFailed}
                  offerNoticeAbove={ouvert.stage !== "nouveau"}
                  onRetry={() => {
                    void lireDossier(ouvert.id).catch(() => undefined);
                  }}
                />
              )
            }
            briefing={
              ouvert.stage === "nouveau" ? (
                <Briefing
                  file={{
                    receivedAt: ouvert.submittedAt ?? ouvert.stageEnteredAt,
                    depositPct: ouvert.depositPct,
                  }}
                  analysis={analysis}
                  standing={runStanding(analysis, analysisLoaded, analysisUnavailable)}
                  sentence={analysisSentence}
                  busy={busy}
                  refusalReasons={refusalReasons}
                  onRefuse={(reasonCode) => {
                    refuseFile(ouvert.id, reasonCode);
                  }}
                  onPrendre={() => {
                    void suivre(
                      api.request<unknown>(`/api/v1/bank/files/{id}/review`, "post", {
                        params: { id: ouvert.id },
                      }),
                    ).then(async () => {
                      await charger();
                      await lireDossier(ouvert.id);
                    });
                  }}
                />
              ) : undefined
            }
          >
            {refusal === null ? null : (
              <p role="alert" className="mb-3 text-sm text-rose-600" data-testid="bank-revue-refus">
                {refusal}
              </p>
            )}
            {offerFailed && ouvert.stage !== "nouveau" ? (
              // `SYNTHESE-READ-FAILURE` (B): from « revue » on the notice stands above the stage's panels; at « nouveau » it stands in the synthesis.
              <OfferReadFailure
                onRetry={() => {
                  void lireDossier(ouvert.id).catch(() => undefined);
                }}
              />
            ) : null}
            {ouvert.stage === "revue" && composer?.reviewComplete === true ? (
              // A returned file whose sixteen points stand opens on the composer: the block rides above it.
              <Instructions
                instructions={offerRead?.instructions ?? []}
                busy={busy}
                onTreated={(instructionId) => {
                  void api
                    .request<unknown>(
                      "/api/v1/bank/files/{id}/instructions/{instructionId}/treated",
                      "post",
                      {
                        params: { id: ouvert.id, instructionId },
                      },
                    )
                    .then(() => lireDossier(ouvert.id))
                    .catch(() => undefined);
                }}
              />
            ) : null}
            {ouvert.stage === "retour" ? (
              <RetourPanel completion={completion} />
            ) : ouvert.stage === "refuse" ? (
              // D2 frame 51: the callout, « Le chemin parcouru », « Ce que Hakili avait lu »; no « Rouvrir » (D3, D-1019-2).
              <RefuseView
                refusal={refusalRead}
                receivedAt={ouvert.submittedAt ?? ouvert.stageEnteredAt}
                handled={progression?.handled ?? null}
                reading={closReading(analysis)}
              />
            ) : ouvert.stage === "validation" && offerRead !== null ? (
              <ValidationPanel read={offerRead} />
            ) : ouvert.stage === "offre" && offerRead !== null ? (
              <OfferPanel read={offerRead} banksReached={banksReached} />
            ) : ouvert.stage === "perdu" ? (
              // D2 frame 50: « Dossier clos. », « Le chemin parcouru », « Dernière offre transmise »: the offer card and the register
              // banner of the old panel are extras. A study withdrawn before any offer has no offer line and no last-offer card.
              <PerduView
                lost={lostRead}
                receivedAt={ouvert.submittedAt ?? ouvert.stageEnteredAt}
                offer={lastOfferFrom(offerRead, offerTransmittedAt)}
              />
            ) : ouvert.stage === "conditions" && conditionsRead !== null ? (
              // `T-1b`, `M1` (WF-BNK-16): the conditions panel to the drawing, each lift by its door.
              <ConditionsPanel
                conditions={conditionsRead}
                disbursement={disbursementRead}
                laterObligations={laterObligations}
                busy={busy}
                onLift={(key) => {
                  void suivre(
                    api.request<unknown>("/api/v1/bank/files/{id}/conditions/{key}/lift", "post", {
                      params: { id: ouvert.id, key },
                      // `WORKSPACE-SEND-REFERENCE`: the reference of the file DRAWN; the door refuses (409) where it is not the file's own.
                      body: { seenReference: ouvert.reference },
                    }),
                  ).then(() => lireDossier(ouvert.id));
                }}
                ordre={ordreRead}
                onOrder={() => {
                  // `T-2`, WF-BNK-16 step 6: « Ordonner les deux virements », the analyst's first act.
                  void suivre(
                    api.request<unknown>("/api/v1/bank/files/{id}/disburse", "post", {
                      params: { id: ouvert.id },
                      // `WORKSPACE-SEND-REFERENCE` round 2: the reference of the file DRAWN; the door refuses (409) where it is not the file's own.
                      body: { seenReference: ouvert.reference },
                    }),
                  ).then(() => lireDossier(ouvert.id));
                }}
                fundsMoment={fonds.moment}
                onReload={() => {
                  globalThis.location.reload();
                }}
                onFunds={(dealerTransferReference, hakiliTransferReference) => {
                  // `T-2`, WF-BNK-17: « Fonds versés », the second act, the two transfers' references.
                  // `ODF-1`: the answer is followed by the page's own re-reads until the file is `décaissé` (`fonds`).
                  setRefusal(null);
                  void fonds
                    .run(
                      async () => {
                        await api.request<unknown>("/api/v1/bank/files/{id}/funds", "post", {
                          params: { id: ouvert.id },
                          body: {
                            dealerTransferReference,
                            hakiliTransferReference,
                            seenReference: ouvert.reference,
                          },
                        });
                      },
                      async () => {
                        await charger();
                        await lireDossier(ouvert.id);
                      },
                    )
                    .catch((failure: unknown) => {
                      setRefusal(
                        (failure as { message?: string } | null)?.message ??
                          t("common.error.connexionEchouee"),
                      );
                    });
                }}
              />
            ) : ouvert.stage === "decaisse" ? (
              // `T-2`, `P2-BKW-006` (WF-BNK-17): the terminal state, one row per transfer.
              <DecaissePanel
                ordre={ordreRead}
                loading={ordreEnLecture}
                laterObligations={laterObligations}
                parcours={{
                  submittedAt: ouvert.submittedAt,
                  transmittedAt: offerTransmittedAt,
                  contractSignedAt: disbursementRead?.contractSignedAt ?? null,
                }}
              />
            ) : ouvert.stage === "signature" && offerRead !== null && signatureRead !== null ? (
              // `T-1`, `M1` (WF-BNK-15): the signature panel to the drawing, every act by its door.
              <SignaturePanel
                read={offerRead}
                signature={signatureRead}
                clientName={ouvert.clientName ?? ""}
                bankName={session.identity.organisation}
                busy={busy}
                onSendPack={() => {
                  void suivre(
                    api.request<unknown>("/api/v1/bank/files/{id}/signature-pack", "post", {
                      params: { id: ouvert.id },
                    }),
                  ).then(() => lireDossier(ouvert.id));
                }}
                onSetAppointment={(agence, when) => {
                  void suivre(
                    api.request<unknown>("/api/v1/bank/files/{id}/signature-appointment", "put", {
                      params: { id: ouvert.id },
                      body: { agence, when },
                    }),
                  ).then(() => lireDossier(ouvert.id));
                }}
                onMarkSigned={(documentId, scan) => {
                  void markSigned(ouvert.id, documentId, scan);
                }}
                onProceed={() => {
                  void suivre(
                    api.request<unknown>("/api/v1/bank/files/{id}/conditions", "post", {
                      params: { id: ouvert.id },
                    }),
                  ).then(async () => {
                    await charger();
                    await lireDossier(ouvert.id);
                  });
                }}
              />
            ) : ouvert.stage === "comite" && offerRead !== null ? (
              // `COMITE-RECORD-3` (frames 43 and 44, D-1026-1): the analyst's committee stage waits, and says so. D2 draws the bar, the
              // adjournment's card and the two cards « Offre acceptée par le client » / « Ce que devient le dossier », and nothing of the
              // offer panel: the offer's terms ride inside the first card.
              <CommitteeWait
                committee={offerRead.committee ?? null}
                offer={
                  offerRead.offer === null
                    ? null
                    : {
                        totalFinancedAmount: offerRead.offer.recap.totalFinancedAmount,
                        durationMonths: offerRead.offer.durationMonths,
                        nominalRatePct: offerRead.offer.nominalRatePct,
                      }
                }
              />
            ) : ouvert.stage === "signature" && offerRead !== null ? (
              // F5: after the buyer's choice , the panel with its answered status line,
              // and the file's new stage rendered unbuilt with its title and its round.
              <OfferPanel
                read={offerRead}
                banksReached={banksReached}
                after={<LaterStagePanel stage={ouvert.stage} />}
              />
            ) : offerFailed && OFFER_PANEL_STAGES.has(ouvert.stage) ? null : composer?.reviewComplete ===
                true && backToReview !== ouvert.id ? (
              <Composer
                clientName={ouvert.clientName ?? ""}
                reference={ouvert.reference}
                facts={composer.facts}
                stored={composer.stored}
                busy={busy}
                proposal={analysis?.offer ?? null}
                takeProposal={proposalTaken}
                ownFile={composer.ownFile}
                onReassign={() => {
                  naviguer("/equipe");
                }}
                offerReason={analysis?.offerReason ?? null}
                gradeIncomplete={analysis?.grade?.incomplete ?? null}
                gradeProvisional={analysis?.grade?.provisional === true}
                onRevenir={() => {
                  setBackToReview(ouvert.id);
                }}
                onFlagging={setComposerFlagging}
                termNote={analysis?.termNote ?? null}
                analysisSentence={analysisSentence}
                onPolitique={() => {
                  setPolicyFrom(ouvert.reference);
                  naviguer("/politique");
                }}
                onSend={(input) => {
                  // « Envoyer au chef du crédit »: the composition saved by its door, then sent by the next. For the chef who holds the file (`ownFile`
                  // served), the next door is the ONE gesture `validate-and-send`, and only when the PUT's own answer still serves a route that sends.
                  const holdsAsChief = composer.ownFile !== null;
                  void suivre(
                    api
                      .request<unknown>("/api/v1/bank/files/{id}/offer", "put", {
                        params: { id: ouvert.id },
                        // `WORKSPACE-SEND-REFERENCE`: the reference of the file DRAWN rides with the composition.
                        body: { ...input, seenReference: ouvert.reference },
                      })
                      .then((saved) => {
                        if (!holdsAsChief) {
                          return api.request<unknown>("/api/v1/bank/files/{id}/offer/submit", "post", {
                            params: { id: ouvert.id },
                            // `WORKSPACE-SEND-REFERENCE` round 2: the submit names the file DRAWN too, beside the composition's own save.
                            body: { seenReference: ouvert.reference },
                          });
                        }
                        const route = ownFileFrom(saved)?.route;
                        // Both chef doors name the file DRAWN (`seenReference`), taken from the read the chef actually saw.
                        if (route === "validateAndSend" || route === "aboveDelegation") {
                          return api.request<unknown>(
                            "/api/v1/bank/files/{id}/offer/validate-and-send",
                            "post",
                            {
                              params: { id: ouvert.id },
                              body: { seenReference: ouvert.reference },
                            },
                          );
                        }
                        return route === "sendToCommittee"
                          ? api.request<unknown>("/api/v1/bank/files/{id}/offer/send-to-committee", "post", {
                              params: { id: ouvert.id },
                              body: { seenReference: ouvert.reference },
                            })
                          : undefined;
                      }),
                  ).then(async () => {
                    await charger();
                    await lireDossier(ouvert.id);
                  });
                }}
              />
            ) : (
              <Revue
                onPolitique={() => {
                  naviguer("/politique");
                }}
                points={points}
                progression={progression}
                busy={busy}
                onPoint={(pointId, verdict) => {
                  // `U-2`: her act on the point rides the same door; the analysis re-read shows it beside the proposal.
                  void suivre(
                    api.request<unknown>(`/api/v1/bank/files/{id}/review/{pointId}`, "put", {
                      params: { id: ouvert.id, pointId },
                      body: verdict,
                    }),
                  ).then(() => lireAnalyse(ouvert.id));
                }}
                analysis={analysis}
                analysisSentence={analysisSentence}
                onTerminer={() => {
                  // `S3`: the review was completed already (the file's own word), so « Terminer la revue et préparer l'offre » only brings the composer back.
                  if (composer?.reviewComplete === true) {
                    setBackToReview(null);
                    return;
                  }
                  void suivre(
                    api.request<unknown>(`/api/v1/bank/files/{id}/review/complete`, "post", {
                      params: { id: ouvert.id },
                    }),
                  ).then(async () => {
                    await charger();
                    await lireDossier(ouvert.id);
                  });
                }}
                refusalReasons={refusalReasons}
                onRefuse={(reasonCode) => {
                  refuseFile(ouvert.id, reasonCode);
                }}
                onAsk={(request) => {
                  // WF-BNK-07: the request by the analyst's own door; the file at `retour` on the re-read.
                  void followDecision(
                    api.request<unknown>("/api/v1/bank/files/{id}/completion-request", "post", {
                      params: { id: ouvert.id },
                      body: request,
                    }),
                  ).then(async () => {
                    await charger();
                    await lireDossier(ouvert.id);
                  });
                }}
                instructions={offerRead?.instructions ?? []}
                onInstructionTreated={(instructionId) => {
                  // « Marquer comme traitées »: the analyst's own door, one row at a time.
                  void api
                    .request<unknown>(
                      "/api/v1/bank/files/{id}/instructions/{instructionId}/treated",
                      "post",
                      {
                        params: { id: ouvert.id, instructionId },
                      },
                    )
                    .then(() => lireDossier(ouvert.id))
                    .catch(() => undefined);
                }}
                documentDoor={async (documentCode) => {
                  // The door opens a received pièce by its code; a 404 is the one answer for every refusing case (another
                  // bank, a refus, a withdrawn file, an unknown or unreceived pièce, no file): the viewer says « indisponible ».
                  try {
                    const answer = await api.request<unknown>(
                      "/api/v1/bank/files/{id}/pieces/{code}/file",
                      "get",
                      {
                        params: { id: ouvert.id, code: documentCode },
                      },
                    );
                    return documentFilesFrom(answer);
                  } catch (failure) {
                    if (failure instanceof ApiError && failure.status === 404) return null;
                    throw failure;
                  }
                }}
              />
            )}
          </Workspace>
        ) : null}
        {wide && vue === "workspace" && ouvert !== null ? (
          <WideConversation
            thread={thread}
            asking={asking}
            refusal={questionRefusal}
            asked={lastAsked}
            modal={narrow}
            subtitle={ouvert.clientName === null ? null : `${ouvert.clientName} · ${ouvert.reference}`}
            documents={receivedDocuments(analysis)}
            report={reportRead !== null}
            suspended={stripView !== null}
            onDocument={(code, label) => {
              setStripView((now) => ({
                title: label,
                opening: (now?.opening ?? 0) + 1,
                returnTo: `bank-conversation-piece-${code}`,
                // The door is asked on every opening (a signed link lives 900 seconds); a 404 is « indisponible », never an error page.
                door: async () => {
                  try {
                    const answer = await api.request<unknown>(
                      "/api/v1/bank/files/{id}/pieces/{code}/file",
                      "get",
                      {
                        params: { id: ouvert.id, code },
                      },
                    );
                    return documentFilesFrom(answer);
                  } catch (failure) {
                    if (failure instanceof ApiError && failure.status === 404) return null;
                    throw failure;
                  }
                },
              }));
            }}
            onReport={() => {
              setStripView((now) => ({
                title: t("bank.conversation.rapport"),
                opening: (now?.opening ?? 0) + 1,
                returnTo: "bank-conversation-rapport",
                door: async () => {
                  const answer = await api.request<unknown>("/api/v1/bank/files/{id}/rapport", "get", {
                    params: { id: ouvert.id },
                  });
                  return reportFilesFrom(answer);
                },
              }));
            }}
            onAsk={(question) => {
              void askQuestion(ouvert.id, question);
            }}
            onClose={() => {
              setWide(false);
              // D2 (frame 222, `wide-close`): the focus returns to « Interroger Hakili ».
              window.setTimeout(() => {
                document.querySelector<HTMLElement>('[data-testid="bank-question-interroger"]')?.focus();
              }, 0);
            }}
          />
        ) : null}
        {stripView === null ? null : (
          <DocumentViewer
            key={stripView.opening}
            title={stripView.title}
            source={null}
            door={stripView.door}
            verdict={null}
            checks={[]}
            reason={null}
            acts={null}
            fromConversation
            onClose={() => {
              const back = stripView.returnTo;
              setStripView(null);
              // Frame 180: the focus is on the chip that was pressed, nothing else has moved (the panel stayed mounted).
              window.setTimeout(() => {
                document.querySelector<HTMLElement>(`[data-testid="${back}"]`)?.focus();
              }, 0);
            }}
          />
        )}
        <PersonaSwitcher demo={demo} persona={persona} choisir={setPersonaDemo} />
      </BankShell>
    </DemandeFonctionnaliteProvider>
  );
}
